import { RigModel, dots, hex, idHash, scale, type Animator, type Color, type Form, type Palette, type Part, type PathOp, type Pose, type Shape, type Vec } from "./Rig";
import { pixelModel } from "./pixelArt";

/**
 * Space whales, side on, nose toward +x: a long body with a darker back, a
 * pale grooved throat, an eye and the long line of the mouth, a flipper
 * that sweeps and a tail that beats. Three kinds, by id:
 * - barnacled: blue-grey, knobs and barnacles crusting its back, a row of
 *   glowing spots down its flank;
 * - lamp: near black, of the dark reaches, a lit lure on a stalk from its
 *   brow and light along its throat grooves and flank;
 * - rockback: grey and stubbier, boulders and lichen grown over its back
 *   like a drifting rock that turns out to be alive.
 * Angry, every glow (and its eye) burns red; see whalePalette.
 */

export type WhaleVariant = "barnacled" | "lamp" | "rockback";
export const WHALE_VARIANTS: readonly WhaleVariant[] = ["barnacled", "lamp", "rockback"];

export function whaleVariant(id: string): WhaleVariant {
  const h = idHash(id, 5);
  return h < 1 / 3 ? "barnacled" : h < 2 / 3 ? "lamp" : "rockback";
}

/** WHALES.radius. */
const R = 6;

/** The glowing colours, which change when it's angry. */
export const WHALE_GLOWS = ["pupil", "spot", "lamp", "crack"] as const;

const fluke = (k: number): Form => ({
  kind: "path",
  ops: [["M", 0, 0], ["Q", -R * 0.4 * k, R * 0.7 * k, -R * 0.85 * k, R * 0.85 * k], ["Q", -R * 0.6 * k, R * 0.3 * k, -R * 0.62 * k, 0], ["Q", -R * 0.6 * k, -R * 0.3 * k, -R * 0.85 * k, -R * 0.85 * k], ["Q", -R * 0.4 * k, -R * 0.7 * k, 0, 0], ["Z"]],
});

/** The whale's outline: a long rounded body tapering to the tail; its darker back; its pale throat. */
const BODY: PathOp[] = [["M", R * 1.4, 0], ["C", R * 1.4, R * 0.8, R * 0.2, R * 0.9, -R * 0.6, R * 0.5], ["C", -R * 1.2, R * 0.25, -R * 1.6, R * 0.1, -R * 1.9, 0], ["C", -R * 1.6, -R * 0.1, -R * 1.2, -R * 0.3, -R * 0.6, -R * 0.55], ["C", R * 0.2, -R * 0.85, R * 1.4, -R * 0.7, R * 1.4, 0], ["Z"]];
const BACK: PathOp[] = [["M", R * 1.25, R * 0.42], ["C", R * 0.9, R * 0.78, R * 0.2, R * 0.88, -R * 0.6, R * 0.5], ["C", -R * 1.2, R * 0.25, -R * 1.6, R * 0.1, -R * 1.9, 0], ["C", -R * 1.5, R * 0.02, -R * 1.1, R * 0.08, -R * 0.6, R * 0.2], ["C", R * 0.1, R * 0.45, R * 0.8, R * 0.42, R * 1.25, R * 0.42], ["Z"]];
const BELLY: PathOp[] = [["M", R * 1.2, -R * 0.2], ["C", R * 0.8, -R * 0.7, -R * 0.2, -R * 0.75, -R * 0.8, -R * 0.4], ["L", R * 1.2, -R * 0.2], ["Z"]];
/** Grooves down its throat, from under the jaw back along the belly. */
const pleat = (i: number, from = 1.15, to = 0.3, y0 = 0.28): PathOp[] => {
  const y = -R * (y0 + i * 0.075);
  return [["M", R * (from - i * 0.07), y + R * 0.06], ["Q", R * 0.4, y - R * 0.12, -R * (to - i * 0.05), y + R * 0.02]];
};
const PLEATS: PathOp[] = [0, 1, 2, 3, 4].flatMap((i) => pleat(i));
const MOUTH: PathOp[] = [["M", R * 1.39, -R * 0.06], ["Q", R * 1.1, -R * 0.24, R * 0.72, -R * 0.2]];
const FLIPPER: PathOp[] = [["M", R * 0.12, 0], ["Q", -R * 0.1, -R * 0.35, -R * 0.65, -R * 0.55], ["Q", -R * 0.35, -R * 0.2, -R * 0.12, 0], ["Z"]];
/** Knobs along its back, and barnacles crusting its head. */
const KNOBS: [number, number, number][] = [[-0.15, 0.66, 0.06], [-0.45, 0.56, 0.055], [-0.75, 0.43, 0.05], [-1.05, 0.3, 0.045]];
const BARNACLES: [number, number, number][] = [[1.02, 0.5, 0.05], [0.9, 0.58, 0.035], [1.15, 0.38, 0.03], [0.78, 0.62, 0.04], [0.98, 0.42, 0.025]];
const disc = (color: string, [x, y, r]: [number, number, number]): Shape => ({ color, at: [R * x, R * y], form: { kind: "disc", r: R * r } });
/** Boulders grown along the rockback's back: x, y, half width, height. */
const STONES: [number, number, number, number][] = [[-0.2, 0.75, 0.3, 0.2], [-0.7, 0.55, 0.24, 0.16], [0.35, 0.68, 0.18, 0.13], [-1.15, 0.33, 0.16, 0.11]];

function whaleRig(v: WhaleVariant): Part[] {
  const parts: Part[] = [
    { name: "tail", at: [-R * 1.8, 0], z: 0, moves: true, shapes: [{ color: "body", form: fluke(v === "lamp" ? 1.1 : v === "rockback" ? 0.85 : 1) }] },
    {
      name: "body",
      at: [0, 0],
      z: 1,
      shapes: [
        { color: "body", form: { kind: "path", ops: BODY } },
        { color: "back", form: { kind: "path", ops: BACK } },
        { color: "belly", form: { kind: "path", ops: BELLY } },
        { color: "pleat", form: { kind: "stroke", width: 0.08, ops: PLEATS } },
        { color: "mouth", form: { kind: "stroke", width: 0.1, ops: MOUTH } },
        ...(v === "barnacled" ? KNOBS.map((k) => disc("back", k)) : []),
        ...BARNACLES.map((b) => disc("barnacle", b)),
        disc("rim", [0.98, 0.02, 0.085]),
      ],
    },
    { name: "pupil", at: [0, 0], z: 2, shapes: [disc("pupil", [0.99, 0.02, 0.055])] },
  ];
  if (v === "barnacled") parts.push({ name: "spots", at: [0, 0], z: 3, shapes: [{ color: "spot", form: dots([0, 1, 2, 3, 4, 5].map((i): Vec => [R * (0.7 - i * 0.36), R * (0.18 - i * 0.025)]), R * 0.07) }] });
  if (v === "lamp") {
    parts.push(
      { name: "throat", at: [0, 0], z: 2, shapes: [{ color: "lamp", form: { kind: "stroke", width: 0.07, ops: [0, 1, 2, 3].flatMap((i) => pleat(i, 1.1, 0.25, 0.3)) } }] },
      { name: "flank", at: [0, 0], z: 3, shapes: [{ color: "lamp", form: dots([0, 1, 2, 3, 4, 5, 6, 7, 8].map((i): Vec => [R * (0.9 - i * 0.3), R * (0.1 + Math.sin(i) * 0.08)]), R * 0.025) }] },
      // The lure, swaying on its stalk ahead of the brow.
      { name: "lure", at: [R * 0.9, R * 0.6], z: 5, moves: true, shapes: [{ color: "back", form: { kind: "stroke", width: 0.12, ops: [["M", 0, 0], ["Q", R * 0.4, R * 0.65, R * 0.8, R * 0.4]] } }, { color: "lamp", at: [R * 0.8, R * 0.4], form: { kind: "disc", r: R * 0.1 } }] },
    );
  }
  if (v === "rockback") {
    const stones: Shape[] = STONES.flatMap(([x, y, w, h]): Shape[] => [
      { color: "rock", form: { kind: "path", ops: [["M", R * (x - w), R * (y - h * 0.3)], ["L", R * (x - w * 0.5), R * (y + h)], ["L", R * (x + w * 0.4), R * (y + h * 0.85)], ["L", R * (x + w), R * (y - h * 0.2)], ["Z"]] } },
      { color: "rockLit", form: { kind: "path", ops: [["M", R * (x - w * 0.5), R * (y + h)], ["L", R * (x + w * 0.4), R * (y + h * 0.85)], ["L", R * x, R * (y + h * 0.45)], ["Z"]] } },
    ]);
    const lichen: Shape[] = Array.from({ length: 8 }, (_, i) => ({ color: "lichen", at: [R * (0.6 - i * 0.28), R * (0.52 - i * 0.045)], form: { kind: "disc", r: R * 0.07, ry: R * 0.035 } }));
    parts.push({ name: "stones", at: [0, 0], z: 3, shapes: [...stones, ...lichen] }, { name: "cracks", at: [0, 0], z: 4, shapes: [{ color: "crack", form: dots(STONES.map(([x, y]): Vec => [R * x, R * (y - 0.02)]), R * 0.03) }] });
  }
  parts.push({ name: "flipper", at: [R * 0.45, -R * 0.42], z: 6, moves: true, shapes: [{ color: "back", form: { kind: "path", ops: FLIPPER } }] });
  return parts;
}

export const WHALE_MODELS: Record<WhaleVariant, RigModel> = {
  barnacled: pixelModel({ parts: whaleRig("barnacled"), glow: [...WHALE_GLOWS] }),
  lamp: pixelModel({ parts: whaleRig("lamp"), glow: [...WHALE_GLOWS] }),
  rockback: pixelModel({ parts: whaleRig("rockback"), glow: [...WHALE_GLOWS] }),
};

const HIDES: Record<WhaleVariant, Record<string, Color>> = {
  barnacled: { body: hex(0x3d5566), back: hex(0x2a3d4b), belly: hex(0x8aa3ad), pleat: hex(0x5f7a86), barnacle: hex(0xa9b6b2), rim: hex(0x9fb4bb) },
  lamp: { body: hex(0x1d2a38), back: hex(0x121b26), belly: hex(0x3a5468), pleat: hex(0x2b4255), barnacle: hex(0x4a6070), rim: hex(0x5e7f93) },
  rockback: { body: hex(0x55524c), back: hex(0x3a3833), belly: hex(0x8d8a80), pleat: hex(0x6e6a62), barnacle: hex(0xa39f92), rim: hex(0x9d9a90) },
};
const ANGRY: Color = [2.6, 0.7, 0.4];
const ROCK = hex(0x4a4642);

/** A kind's colours, calm or angry: angry, its eye and every glow burn red (a rockback's cracks only show then). */
export function whalePalette(v: WhaleVariant, angry: boolean): Palette {
  return {
    ...HIDES[v],
    mouth: hex(0x18242c),
    rock: ROCK,
    rockLit: scale(ROCK, 1.35),
    lichen: [0.55, 0.6, 0.35],
    pupil: angry ? ANGRY : hex(0x0e1418),
    spot: angry ? ANGRY : [0.6, 2.2, 2.0],
    lamp: angry ? ANGRY : [0.7, 1.6, 2.4],
    crack: angry ? ANGRY : ROCK,
  };
}

export interface Temper {
  angry: boolean;
}

/** Swimming: the tail beats and the flipper sweeps, three times as fast when it's angry; a lamp whale's lure sways. */
export class WhaleAnimator implements Animator<Temper> {
  private t: number;

  constructor(seed = Math.random()) {
    this.t = seed * 10;
  }

  step(dt: number, m: Temper): Pose {
    this.t += dt * (m.angry ? 3 : 1);
    return { tail: { rot: Math.sin(this.t * 2.2) * 0.35 }, flipper: { rot: Math.sin(this.t * 2.2 - 1) * 0.25 }, lure: { rot: Math.sin(this.t * 1.3) * 0.15 } };
  }
}
